export type CriteriaField = 'department' | 'location' | 'division' | 'jobTitle' | 'payGroup' | 'workerType' | 'state';
export interface CriteriaOption {value: string; field: CriteriaField; request: number; }
export interface WorkforceCriteria {version: 1; allEligible: boolean; rules: Array<{field: CriteriaField; operator: 'in'; values: string[]}>; }
export interface SavedWorkforceCriteria {teamId: string; managed: boolean; revision: number; criteria: WorkforceCriteria | null; }
export interface CriteriaPreview {
  snapshotId: string; collectedAt: string; complete: boolean; nextCursor: string | null;
  counts: {scannedRows: number; matchingRows: number; eligibleRows: number; blockedRows: number};
  employees: Array<{employeeKey: string; firstName: string; lastName: string; department: string; jobTitle: string; activeEligibility: string}>;
}
export interface MatchingSelection {snapshotId: string; criteria: WorkforceCriteria; revision?: number; eligibleRows: number; }
export interface WorkforceCriteriaApi {
  getWorkforceTeamCriteria(companyId: string, teamId: string): Promise<SavedWorkforceCriteria>;
  saveWorkforceTeamCriteria(companyId: string, teamId: string, body: {revision: number; criteria: WorkforceCriteria}): Promise<SavedWorkforceCriteria>;
  previewWorkforceCriteria(companyId: string, body: any): Promise<CriteriaPreview>;
  getWorkforceCriteriaOptions(companyId: string, body: any): Promise<{values: string[]; next: string | null}>;
}
export class WorkforceTeamCriteriaState {
  readonly fields: CriteriaField[] = ['department', 'location', 'division', 'jobTitle', 'payGroup', 'workerType', 'state'];
  readonly labels: Record<CriteriaField, string> = {department: 'Department', location: 'Location', division: 'Division', jobTitle: 'Payroll Job Title', payGroup: 'Pay Type', workerType: 'Employee Type', state: 'State'};
  criteria: WorkforceCriteria = {version: 1, allEligible: false, rules: []};
  saved: SavedWorkforceCriteria | null = null;
  preview: CriteriaPreview | null = null;
  options: CriteriaOption[] = []; optionsNext: string | null = null; optionsBusy = false;
  private selectedField: CriteriaField = 'department'; prefix = '';
  get field(): CriteriaField { return this.selectedField; }
  set field(value: CriteriaField) {
    if (value === this.selectedField) return;
    this.selectedField = value; ++this.optionsVersion; this.options = []; this.optionsNext = null; this.optionsBusy = false;
  }
  loading = false; busy = false; error = ''; companyId = ''; snapshotId = ''; teamId = '';
  private version = 0; private optionsVersion = 0;
  constructor(private api: WorkforceCriteriaApi, private changed: () => void = () => {}) {}
  destroy(): void { ++this.version; ++this.optionsVersion; this.preview = null; this.options = []; this.optionsNext = null; this.busy = false; this.loading = false; this.optionsBusy = false; this.changed(); }
  async open(companyId: string, snapshotId: string, teamId = ''): Promise<void> {
    this.destroy(); this.companyId = companyId; this.snapshotId = snapshotId; this.teamId = teamId;
    this.saved = null; this.error = ''; this.criteria = {version: 1, allEligible: false, rules: []};
    if (!teamId) return;
    const version = this.version; this.loading = true;
    try {
      const result = await this.api.getWorkforceTeamCriteria(companyId, teamId);
      if (version !== this.version) return;
      this.saved = result; if (result.criteria) this.criteria = JSON.parse(JSON.stringify(result.criteria));
    } catch (error: any) {
      if (version === this.version && (error?.data?.code || error?.error?.code) !== 'team_not_managed') this.error = 'Unable to load saved membership criteria.';
    } finally { if (version === this.version) this.loading = false; }
  }
  get valid(): boolean { return this.criteria.allEligible || this.criteria.rules.length > 0; }
  get dirty(): boolean { return JSON.stringify(this.criteria) !== JSON.stringify(this.saved?.criteria); }
  invalidate(): void { ++this.version; this.preview = null; this.busy = false; this.changed(); }
  setAllEligible(value: boolean): void { this.criteria = {version: 1, allEligible: value, rules: []}; this.invalidate(); }
  add(value: string): void {
    if (!value || !value.trim()) return;
    const normalized = value.trim().toLowerCase();
    let rule = this.criteria.rules.find(item => item.field === this.field);
    if (rule?.values.includes(normalized) || rule?.values.length === 50) return;
    if (!rule) { rule = {field: this.field, operator: 'in', values: []}; this.criteria.rules.push(rule); }
    rule.values.push(normalized); rule.values.sort();
    this.criteria.rules.sort((x, y) => this.fields.indexOf(x.field) - this.fields.indexOf(y.field));
    this.criteria.allEligible = false; this.invalidate();
  }
  addOption(option: CriteriaOption): void {
    if (!option || this.optionsBusy || option.field !== this.field || option.request !== this.optionsVersion || !this.options.includes(option)) return;
    this.add(option.value);
  }
  remove(field: CriteriaField, value: string): void {
    this.criteria.rules = this.criteria.rules.map(rule => rule.field === field ? {...rule, values: rule.values.filter(item => item !== value)} : rule).filter(rule => rule.values.length);
    this.invalidate();
  }
  async loadOptions(more = false): Promise<void> {
    const version = ++this.optionsVersion; this.optionsBusy = true;
    const after = more ? this.optionsNext : null;
    const field = this.field, companyId = this.companyId, snapshotId = this.snapshotId, prefix = this.prefix;
    this.options = []; this.optionsNext = null;
    try {
      const result = await this.api.getWorkforceCriteriaOptions(companyId, {snapshotId, field, prefix, ...(after ? {after} : {})});
      if (version !== this.optionsVersion || field !== this.field || companyId !== this.companyId || snapshotId !== this.snapshotId || prefix !== this.prefix) return;
      this.options = result.values.slice(0, 50).map(value => ({value, field, request: version})); this.optionsNext = result.next;
    } catch (_) { if (version === this.optionsVersion) { this.options = []; this.optionsNext = null; this.error = 'Unable to load criteria values. Reload the snapshot to check access.'; } }
    finally { if (version === this.optionsVersion) this.optionsBusy = false; }
  }
  async save(): Promise<void> {
    if (!this.saved || !this.valid || this.busy) return;
    const version = ++this.version; this.preview = null; this.changed(); this.busy = true; this.error = '';
    try {
      const result = await this.api.saveWorkforceTeamCriteria(this.companyId, this.teamId, {revision: this.saved.revision, criteria: this.criteria});
      if (version !== this.version) return;
      this.saved = result; this.criteria = JSON.parse(JSON.stringify(result.criteria));
    } catch (_) { if (version === this.version) this.error = 'Criteria could not be saved. Reload to review the current revision.'; }
    finally { if (version === this.version) this.busy = false; }
  }
  async loadPreview(): Promise<void> {
    if (!this.valid || this.busy || this.teamId && (!this.saved?.criteria || this.dirty)) return;
    const version = ++this.version; this.preview = null; this.changed(); this.busy = true; this.error = '';
    const criteria = JSON.parse(JSON.stringify(this.criteria));
    let cursor: string | null = null;
    const counts = {scannedRows: 0, matchingRows: 0, eligibleRows: 0, blockedRows: 0};
    let employees: CriteriaPreview['employees'] = [];
    try {
      do {
        const result: CriteriaPreview = await this.api.previewWorkforceCriteria(this.companyId, {snapshotId: this.snapshotId, criteria,
          ...(this.teamId ? {teamId: this.teamId, revision: this.saved!.revision} : {}), ...(cursor ? {cursor} : {})});
        if (version !== this.version) return;
        (Object.keys(counts) as Array<keyof CriteriaPreview['counts']>).forEach(key => counts[key] += result.counts[key]);
        employees = [...employees, ...result.employees].slice(0, 50);
        this.preview = {...result, counts: {...counts}, employees};
        cursor = result.nextCursor;
        if (!result.complete && !cursor) throw new Error('Incomplete preview');
      } while (cursor);
      this.changed();
    } catch (_) { if (version === this.version) { this.preview = null; this.changed(); this.error = 'Preview stopped. Reload the snapshot and review the criteria.'; } }
    finally { if (version === this.version) this.busy = false; }
  }
  get selection(): MatchingSelection | null {
    if (!this.preview?.complete || !this.valid || this.teamId && this.dirty) return null;
    return {snapshotId: this.snapshotId, criteria: JSON.parse(JSON.stringify(this.criteria)), eligibleRows: this.preview.counts.eligibleRows,
      ...(this.teamId ? {revision: this.saved!.revision} : {})};
  }
}
